import { AttachmentId } from "@/modules/local-blob-store/types";

/**
 * Upload state of a cached blob — load-bearing for eviction (M0.4):
 *
 * - `pending`: the LBS holds the ONLY copy of an offline-created blob that has
 *   not yet been uploaded. NEVER evictable — evicting it loses data.
 * - `uploaded`: a re-fetchable cache entry. LRU-evictable (oldest `lastAccessAt`
 *   first) when the cache needs to make room.
 */
export type LocalBlobUploadState = "pending" | "uploaded";

/**
 * One row in the Local Blob Store index — the authority for "is this blob
 * cached / pending". Code answers those questions from this index, NEVER by
 * listing OPFS.
 *
 * The index is intentionally decoupled from the OPFS byte store in this chunk
 * (M0.2): it tracks metadata only. Wiring the two together is M0.3.
 */
export interface LocalBlobIndexEntry {
  /** Primary key = the `associated_note_attachment_id`. */
  attachmentId: AttachmentId;
  /**
   * Cached so the `getBlobUrl` read path can mint the `Blob` / object URL from
   * the LBS alone, without joining the attachment sync model on every render.
   */
  mimeType: string;
  /** Cache bookkeeping for eviction / LRU size math. */
  sizeBytes: number;
  uploadState: LocalBlobUploadState;
  /** Indexed — the LRU key. Epoch millis of the last read/write. */
  lastAccessAt: number;
}

/** Dexie table name for the Local Blob Store index. */
export const LOCAL_BLOB_INDEX_TABLE_NAME = "localBlobIndex";

/**
 * Dexie index string for the index table. `attachmentId` is the primary key;
 * `uploadState` and `lastAccessAt` are indexed individually. The compound
 * `[uploadState+lastAccessAt]` index lets the LRU-victim query (M0.4) both filter
 * to `uploaded` and walk it in `lastAccessAt`-ascending order straight from the
 * index — no in-memory sort — so eviction can cheaply take just the first N
 * victims.
 */
export const LOCAL_BLOB_INDEX_SCHEMA =
  "attachmentId, uploadState, lastAccessAt, [uploadState+lastAccessAt]";
